// Telling the embedded V2 apps that the viewer's player data has just been refreshed.
//
// WHY THIS EXISTS. The "refresh player data" action goes through the legacy API, which now fetches from the
// game AND persists the snapshot plus a recalculated profile into HotUtilsV2 before it returns. So the moment
// that call completes, V2 genuinely holds newer data — but the embedded apps have no way to know. Without a
// signal they show stale data until the user navigates or reloads.
//
// WHY NOT JUST RELOAD THE IFRAME. Reassigning `iframe.src` is the zero-effort option and it is worse: it
// destroys the app's UI state (filters, scroll, selections, anything in progress), re-runs the whole token
// handshake, and flashes. A message lets each app refetch its own data and keep everything else. Note the
// parent could not call `contentWindow.location.reload()` even if it wanted to — that throws cross-origin.
//
// THE MESSAGE CARRIES NO PAYLOAD, deliberately. No player id (the site does not have one conveniently to hand
// at that point) and no version/timestamp. The refresh takes ~20 seconds and is user-initiated, so duplicate
// or late messages are not worth engineering against — the worst case is one redundant refetch. If that ever
// stops being true, add a version to the message rather than deduping on the receiving side.

interface RegisteredEmbed {
    /** Channel namespace — `player`, `gac`, `tb`, `tw`, `guild`, `events`. Must match the app's own appId. */
    appId: string;
    /** The embedded app's exact origin. Never '*' — see below. */
    targetOrigin: string;
    /** Resolved at send time, not registration time: the iframe's contentWindow is null until it loads. */
    getWindow: () => Window | null;
}

const embeds = new Set<RegisteredEmbed>();

/**
 * Registers a mounted embedded app to receive site-level signals. Returns the unregister function — call it
 * on unmount, or a navigated-away frame stays in the set and every later broadcast posts to a dead window.
 */
export function registerV2Embed(embed: RegisteredEmbed): () => void {
    embeds.add(embed);
    return () => { embeds.delete(embed); };
}

/**
 * Tells every mounted embedded app that player data has been refreshed.
 *
 * Broadcast to all of them because the site does not know what each app is currently displaying. A
 * guild-scoped app is free to ignore it; the receiving hook is opt-in, so an app that does not subscribe
 * simply never sees it.
 *
 * 🔴 Each message goes to that app's EXACT origin, never '*'. A wildcard target would hand the signal to
 * whatever happens to be framed there. It leaks nothing sensitive today — the message has no payload — but
 * the moment someone adds one, the wildcard becomes the bug and nobody re-reads this function.
 */
export function broadcastPlayerDataRefreshed(): void {
    embeds.forEach((embed) => {
        try {
            const target = embed.getWindow();
            if (!target) return;   // iframe not loaded yet: it will fetch fresh data on its own first load
            target.postMessage({ type: `hotutils-${embed.appId}:player-data-refreshed` }, embed.targetOrigin);
        } catch (err) {
            // One bad frame must not stop the others being told. A failure here is never worth surfacing to
            // the user: the refresh itself already succeeded, and this is only a notification about it.
            // eslint-disable-next-line no-console
            console.warn(`Could not notify embedded app '${embed.appId}' of the player data refresh`, err);
        }
    });
}
